import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Company } from "@/types/domain";

// ─── State shape ──────────────────────────────────────────────────
export interface CompanyState {
  activeCompany: Company | null;
  companies: Company[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  activeCompany: null,
  companies: [],
  isLoading: false,
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────────
const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setActiveCompany(state, action: PayloadAction<any>) {
      state.activeCompany = action.payload;
    },

    setCompanies(state, action: PayloadAction<any[]>) {
      state.companies = action.payload;
    },

    updateActiveCompany(state, action: PayloadAction<Partial<Company>>) {
      if (state.activeCompany) {
        state.activeCompany = { ...state.activeCompany, ...action.payload };

        // Keep the companies list in sync
        const idx = state.companies.findIndex(
          (c) => c.id === state.activeCompany?.id,
        );
        if (idx !== -1 && state.activeCompany) {
          state.companies[idx] = state.activeCompany;
        }
      }
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearCompany(state) {
      state.activeCompany = null;
      state.companies = [];
      state.error = null;
    },
  },
});

export const {
  setActiveCompany,
  setCompanies,
  updateActiveCompany,
  setLoading,
  setError,
  clearCompany,
} = companySlice.actions;

export default companySlice.reducer;
